import { z } from 'zod'
import type { MenuData, Config } from '@/types'

// ============================================================================
// Zod Schemas for Validation
// ============================================================================

const LanguageEnum = z.enum(['fr', 'ar', 'en'])

const VariantSchema = z.object({
  id: z.string(),
  name_fr: z.string(),
  name_ar: z.string(),
  name_en: z.string(),
  price: z.number().optional(),
  price_adjustment: z.number().optional(),
})

const ModifierOptionSchema = z.object({
  id: z.string(),
  name_fr: z.string(),
  name_ar: z.string(),
  name_en: z.string(),
  price: z.number(),
})

const ModifierSchema = z.object({
  id: z.string(),
  name_fr: z.string(),
  name_ar: z.string(),
  name_en: z.string(),
  price: z.number(),
  type: z.enum(['checkbox', 'radio']),
  options: z.array(ModifierOptionSchema).optional(),
})

const MenuItemSchema = z.object({
  id: z.string(),
  name_fr: z.string(),
  name_ar: z.string(),
  name_en: z.string(),
  desc_fr: z.string(),
  desc_ar: z.string(),
  desc_en: z.string(),
  price: z.number().nonnegative(),
  photo: z.string().optional(),
  popular: z.boolean(),
  available: z.boolean(),
  variant_pricing: z.enum(['absolute', 'relative']),
  base_price: z.number().optional(),
  variants: z.array(VariantSchema).optional(),
  modifiers: z.array(ModifierSchema).optional(),
  sort_order: z.number(),
})

const SubcategorySchema = z.object({
  id: z.string(),
  name_fr: z.string(),
  name_ar: z.string(),
  name_en: z.string(),
  sort_order: z.number(),
  items: z.array(MenuItemSchema),
});

const CategorySchema = z.object({
  id: z.string(),
  name_fr: z.string(),
  name_ar: z.string(),
  name_en: z.string(),
  sort_order: z.number(),
  subcategories: z.array(SubcategorySchema),
});

export const MenuDataSchema = z.object({
  categories: z.array(CategorySchema),
});

const BusinessInfoSchema = z.object({
  name: z.string().min(1).catch('Menu'),
  tagline: z.string().optional(),
  logo: z.string().optional(),
});

const ThemeSchema = z.object({
  primary: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#8B5E3C').catch('#8B5E3C'),
  secondary: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#F5E6D3').catch('#F5E6D3'),
  accent: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#E07A1F').catch('#E07A1F'),
  background: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#FFFBF5').catch('#FFFBF5'),
  mode: z.enum(['light', 'dark', 'auto']).default('light').catch('light'),
  font: z.string().default('fraunces').catch('fraunces'),
  radius: z.enum(['rounded', 'sharp']).default('rounded').catch('rounded'),
  darkMode: z.enum(['auto', 'manual']).default('auto').catch('auto'),
});

const ContactSchema = z.object({
  phone: z.string().default('').catch(''),
  whatsapp: z.string().default('').catch(''),
  address: z.string().default('').catch(''),
  mapsLink: z.string().default('').catch(''),
  instagram: z.string().default('').catch(''),
  hours: z
    .array(z.object({ day: z.string().catch(''), time: z.string().catch('') }))
    .default([])
    .catch([]),
});

const LanguagesSchema = z.object({
  enabled: z.array(LanguageEnum).default(['fr']).catch(['fr']),
  default: LanguageEnum.default('fr').catch('fr'),
});

const FeaturesSchema = z.object({
  photos: z.boolean().default(true).catch(true),
  search: z.boolean().default(true).catch(true),
  cart: z.boolean().default(true).catch(true),
  whatsappOrdering: z.boolean().default(true).catch(true),
  callButton: z.boolean().default(true).catch(true),
  languageToggle: z.boolean().default(true).catch(true),
  rtlArabic: z.boolean().default(true).catch(true),
  adminPanel: z.boolean().default(false).catch(false),
  analytics: z.boolean().default(false).catch(false),
  favorites: z.boolean().default(true).catch(true),
  tableRouting: z.boolean().default(true).catch(true),
  poweredByOmniRise: z.boolean().default(true).catch(true),
  splashScreen: z.boolean().default(true).catch(true),
});

const DEFAULT_MODULES = {
  base: 0,
  photos: 0,
  search: 0,
  cart: 0,
  english: 0,
  arabic: 0,
  admin: 0,
  analytics: 0,
}

const PricingSchema = z.object({
  modules: z
    .object({
      base: z.number().nonnegative().default(0).catch(0),
      photos: z.number().nonnegative().default(0).catch(0),
      search: z.number().nonnegative().default(0).catch(0),
      cart: z.number().nonnegative().default(0).catch(0),
      english: z.number().nonnegative().default(0).catch(0),
      arabic: z.number().nonnegative().default(0).catch(0),
      admin: z.number().nonnegative().default(0).catch(0),
      analytics: z.number().nonnegative().default(0).catch(0),
    })
    .default(DEFAULT_MODULES)
    .catch(DEFAULT_MODULES),
});

const AdminConfigSchema = z.object({
  password: z.string().min(6).default('changeme').catch('changeme'),
  backendEnabled: z.boolean().default(false).catch(false),
});

// Full section defaults — used both for missing sections (.default) and for
// sections that come in as the wrong type (.catch), since the catch value is
// returned as-is without re-parsing.
const DEFAULT_THEME = {
  primary: '#8B5E3C',
  secondary: '#F5E6D3',
  accent: '#E07A1F',
  background: '#FFFBF5',
  mode: 'light' as const,
  font: 'fraunces',
  radius: 'rounded' as const,
  darkMode: 'auto' as const,
}
const DEFAULT_CONTACT = {
  phone: '',
  whatsapp: '',
  address: '',
  mapsLink: '',
  instagram: '',
  hours: [] as { day: string; time: string }[],
}
const DEFAULT_LANGUAGES = { enabled: ['fr'] as ('fr' | 'ar' | 'en')[], default: 'fr' as const }
const DEFAULT_FEATURES = {
  photos: true,
  search: true,
  cart: true,
  whatsappOrdering: true,
  callButton: true,
  languageToggle: true,
  rtlArabic: true,
  adminPanel: false,
  analytics: false,
  favorites: true,
  tableRouting: true,
  poweredByOmniRise: true,
  splashScreen: true,
}
const DEFAULT_PRICING = { modules: DEFAULT_MODULES }
const DEFAULT_ADMIN = { password: 'changeme', backendEnabled: false }

export const ConfigSchema = z.object({
  business: BusinessInfoSchema,
  theme: ThemeSchema.default(DEFAULT_THEME).catch(DEFAULT_THEME),
  contact: ContactSchema.default(DEFAULT_CONTACT).catch(DEFAULT_CONTACT),
  languages: LanguagesSchema.default(DEFAULT_LANGUAGES).catch(DEFAULT_LANGUAGES),
  features: FeaturesSchema.default(DEFAULT_FEATURES).catch(DEFAULT_FEATURES),
  pricing: PricingSchema.default(DEFAULT_PRICING).catch(DEFAULT_PRICING),
  admin: AdminConfigSchema.default(DEFAULT_ADMIN).catch(DEFAULT_ADMIN),
  version: z.string().default('1.0.0').catch('1.0.0'),
});

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validate menu data against schema
 */
export function validateMenuData(data: unknown):
  | { success: true; data: MenuData }
  | { success: false; errors: z.ZodError } {
  const result = MenuDataSchema.safeParse(data)

  if (result.success) {
    return {
      success: true,
      data: result.data,
    }
  }

  return {
    success: false,
    errors: result.error,
  }
}

/**
 * Validate config against schema
 */
export function validateConfig(data: unknown):
  | { success: true; data: Config }
  | { success: false; errors: z.ZodError } {
  const result = ConfigSchema.safeParse(data)

  if (result.success) {
    return {
      success: true,
      data: result.data,
    }
  }

  return {
    success: false,
    errors: result.error,
  }
}

/**
 * Format Zod errors for display
 */
export function formatZodErrors(error: z.ZodError): string[] {
  return error.errors.map((err) => {
    const path = err.path.join('.')
    return `${path}: ${err.message}`
  })
}

/**
 * Get a user-friendly error message for validation failures
 */
export function getValidationErrorMessage(error: z.ZodError): string {
  const errors = formatZodErrors(error)
  return `Validation failed:\n${errors.join('\n')}`
}
